import React from 'react';
import { createPortal } from 'react-dom';
import { motion } from 'framer-motion';
import { Target, Users, TrendingUp, ArrowRight } from 'lucide-react';

const WelcomeStep = ({ onNext, onSkip, onTryAI }) => {
    const features = [
        { 
            icon: Target, 
            title: 'Find Hidden Jobs', 
            desc: 'Identify companies about to hire before the competition' 
        },
        { 
            icon: Users, 
            title: 'Skip Cold Applications', 
            desc: 'We find employees who can refer you' 
        },
        { 
            icon: TrendingUp, 
            title: 'Gain Leverage', 
            desc: 'Daily high-value tasks every morning' 
        }
    ];

    const content = (
        <>
            <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[99998]" />
            <div className="fixed inset-0 z-[99999] flex items-center justify-center pointer-events-none p-4">
                <motion.div
                    initial={{ opacity: 0, scale: 0.96, y: 10 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.96, y: 10 }}
                    transition={{ duration: 0.25, ease: "easeOut" }}
                    className="relative bg-white rounded-2xl shadow-xl border border-gray-200 p-8 max-w-2xl w-full pointer-events-auto"
                >
                    <div className="text-center">
        
                        {/* Title */}
                        <h2 className="text-2xl font-semibold text-gray-900 mb-3">
                            The Job You Want Isn’t on a Job Board Yet
                        </h2>
        
                        {/* Subtitle */}
                        <p className="text-sm text-gray-600 mb-8 leading-relaxed">
                            We surface companies hiring before they post and connect you to employees who can get you in
                        </p>
        
                        {/* Features - minimal grid */}
                        <div className="grid grid-cols-3 gap-4 mb-8">
                            {features.map((item, index) => (
                                <div
                                    key={index}
                                    className="text-center"
                                >
                                    <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center mx-auto mb-2">
                                        <item.icon className="text-blue-600" size={20} />
                                    </div>
                                    <h4 className="text-xs font-semibold text-gray-900 mb-1">{item.title}</h4>
                                    <p className="text-xs text-gray-500 leading-tight">{item.desc}</p>
                                </div>
                            ))}
                        </div>
        
                        {/* CTA Button */}
                        <div className="flex items-center gap-3 justify-center">
                            {onSkip && (
                                <button
                                    onClick={onSkip}
                                    className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900 font-medium transition-colors"
                                >
                                    Skip
                                </button>
                            )}
                            <button
                                onClick={onTryAI || onNext}
                                className="flex items-center gap-2 px-6 py-2.5 bg-blue-600 text-white rounded-lg font-medium text-sm hover:bg-blue-700 transition-colors"
                            >
                                Get Started
                                <ArrowRight size={16} />
                            </button>
                        </div>
                    </div>
                </motion.div>
            </div>
        </>
    );
    
    return typeof document !== 'undefined' 
        ? createPortal(content, document.body)
        : null;
};

export default WelcomeStep;
